import { useQuery, useMutation, createConnectQueryKey, useTransport, createQueryOptions } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { getMe, updateMe } from '../gen/cortado/v1/cortado-CortadoService_connectquery'
import { create } from '@bufbuild/protobuf'
import { GetMeRequestSchema, GetMeResponseSchema } from '../gen/cortado/v1/users_pb'
import { useStore } from '@tanstack/react-store'
import { authStore } from './auth.store'
import type { Transport } from '@connectrpc/connect'
import { connectTransport } from '../lib/connect-transport'

/**
 * Query options for the GetMe RPC, for use in route loaders via
 * queryClient.ensureQueryData/prefetchQuery. Uses the same connectTransport
 * singleton as useCurrentUser so the cache key matches.
 */
export function getMeQueryOptions(transport: Transport = connectTransport) {
  return createQueryOptions(getMe, create(GetMeRequestSchema, {}), { transport })
}

/**
 * Query key for the current user data
 * Used for cache invalidation and management
 */
export const CURRENT_USER_QUERY_KEY = 'currentUser'

/**
 * Hook to fetch and manage the current authenticated user's data
 * 
 * This hook:
 * - Automatically fetches user data when authenticated
 * - Only runs when a valid session exists
 * - Uses Connect-Query for type-safe RPC calls
 * - Integrates with TanStack Query for caching
 * 
 * @returns Query result with user data, loading state, and error
 */
export function useCurrentUser() {
  // Get current auth state reactively
  const session = useStore(authStore, (state) => state.session)
  const isAuthenticated = !!session

  // Use Connect-Query to call GetMe RPC
  const query = useQuery(
    getMe,
    create(GetMeRequestSchema, {}),
    {
      enabled: isAuthenticated,
    }
  )

  return {
    user: query.data?.user,
    // Onboarding state from GetMe response
    profileComplete: query.data?.profileComplete,
    hasOrganization: query.data?.hasOrganization,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to update the current user's profile
 *
 * @returns Mutation for updating user profile
 */
export function useUpdateMe() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateMe, {
    onSuccess: (response) => {
      if (!response.user) return

      // Update the user cache
      const queryKey = createConnectQueryKey({
        schema: getMe,
        transport,
        input: {},
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetMeResponseSchema, {
          user: response.user,
          profileComplete: response.profileComplete,
          hasOrganization: response.hasOrganization,
        })
      )
    },
  })

  return {
    updateProfile: mutation.mutate,
    updateProfileAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  }
}

